"use client";

import type { ReactNode } from "react";

import PageSection from "@ui/components/composers/PageSection";
import { PricingCardsList } from "@ui/components/pricing/PricingCardsList";
import { EGGSHELL } from "@ui/tokens/pageColors";

interface PricingCardsSectionProps {
  showStarterPromo?: boolean;
  variant?: "unified" | "split";
  markerBackgroundColor?: string;
  /** Controls the border/decoration style of the pricing sections. Use 'none' to hide sidelines and edge lines. */
  borderStyle?: "default" | "none";
  /** Whether to show the footer with pricing notice. Defaults to true. */
  showFooter?: boolean;
  /** Whether to show the credits included section at the bottom of each card. Defaults to true. */
  showCreditsIncluded?: boolean;
}

/** Reusable PageSection wrapper with common pricing card section props */
const CardsSection = ({
  children,
  showBorders,
  markerBackgroundColor,
}: {
  children: ReactNode;
  showBorders: boolean;
  markerBackgroundColor: string;
}) => (
  <PageSection
    withSideLines={showBorders}
    withFilledLine
    topPadding='none'
    bottomPadding='none'
    horizontalPadding='none'
    withGap={false}
    markerBackgroundColor={markerBackgroundColor}
    withEdgeLines={showBorders}
  >
    {children}
  </PageSection>
);

export const PricingCardsSection = ({
  showStarterPromo = false,
  variant = "unified",
  markerBackgroundColor = EGGSHELL,
  borderStyle = "default",
  showFooter = true,
  showCreditsIncluded = true,
}: PricingCardsSectionProps) => {
  const showBorders = borderStyle !== "none";
  const sectionProps = { showBorders, markerBackgroundColor };

  // Render split layout (individual + business tiers separately)
  const renderSplitContent = () => (
    <>
      <CardsSection {...sectionProps}>
        <PricingCardsList
          showStarterPromo={showStarterPromo}
          filterTiers='individual'
          showFooter={false}
          showCreditsIncluded={showCreditsIncluded}
        />
      </CardsSection>
      <CardsSection {...sectionProps}>
        <PricingCardsList
          showStarterPromo={false}
          filterTiers='business'
          showFooter={true}
          showCreditsIncluded={showCreditsIncluded}
        />
      </CardsSection>
    </>
  );

  // Render unified layout (all tiers together)
  const renderUnifiedContent = () => (
    <CardsSection {...sectionProps}>
      <PricingCardsList
        showStarterPromo={showStarterPromo}
        showFooter={showFooter}
        showCreditsIncluded={showCreditsIncluded}
      />
    </CardsSection>
  );

  // Platform tabs / billing controls are rendered by the shell — this only renders the cards.
  return <div className='container'>{variant === "split" ? renderSplitContent() : renderUnifiedContent()}</div>;
};
